export type RetryScope = "all" | "walls" | "furniture";
export interface RetryBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

import type { MapCanvasValue } from "../components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import { pausePolling } from "./pausePolling";

export interface RegionalImportSnapshot {
  status: "in_progress" | "partial" | "completed";
  session: string;
  revision: number;
  model: string;
  map?: MapCanvasValue;
  warnings: string[];
  elapsedMs: number;
  error?: string;
  local?: {
    preprocessMs: number;
    mergeMs: number;
    repairMs: number;
    repairs: number;
  };
  parts: {
    id: string;
    label: string;
    status: string;
    error?: string;
    elapsedMs?: number;
    bounds?: RetryBounds;
    hasResult?: boolean;
    patch?: boolean;
    scope?: RetryScope;
    usage?: {
      input_tokens?: number;
      output_tokens?: number;
      output_tokens_details?: { reasoning_tokens?: number };
    };
  }[];
}
/** Region retry is explicit. Status/reconnect requests never create paid jobs. */
export async function requestRegionalFloorplan(options: {
  png?: Blob;
  session?: string;
  retry?: string;
  scope?: RetryScope;
  bounds?: RetryBounds;
  signal: AbortSignal;
  onUpdate: (snapshot: RegionalImportSnapshot) => void;
}) {
  let session = options.session;
  try {
    const params = new URLSearchParams(
      session ? { session } : { engine: "regions" }
    );
    if (options.retry) {
      params.set("retry", options.retry);
      if (options.scope && options.scope !== "all")
        params.set("scope", options.scope);
      if (options.bounds) params.set("bounds", JSON.stringify(options.bounds));
    }
    let response = await fetch(`/api/map-import?${params}`, {
      method: "POST",
      signal: options.signal,
      ...(session
        ? {}
        : { headers: { "Content-Type": "image/png" }, body: options.png })
    });
    while (true) {
      const data = await response.json();
      if (!response.ok) throw Error(data.error || "Regional import failed");
      if (!data.session) throw Error("No regional import session was returned");
      options.signal.throwIfAborted();
      session = data.session;
      options.onUpdate(data);
      if (data.status !== "in_progress") return data as RegionalImportSnapshot;
      await pausePolling(options.signal);
      // Retry transport failures only on read-only polls, never on paid starts.
      for (let attempt = 0; ; attempt++) {
        try {
          response = await fetch(
            `/api/map-import?session=${encodeURIComponent(session!)}`,
            { method: "POST", signal: options.signal }
          );
          if (response.status >= 500 && attempt < 2) {
            await pausePolling(options.signal);
            continue;
          }
          break;
        } catch (e) {
          if (options.signal.aborted || attempt >= 2) throw e;
          await pausePolling(options.signal);
        }
      }
    }
  } finally {
    if (session && options.signal.aborted)
      await fetch(`/api/map-import?session=${encodeURIComponent(session)}`, {
        method: "DELETE",
        keepalive: true,
        signal: AbortSignal.timeout(10000)
      }).catch(() => {});
  }
}

export async function cancelRegionalFloorplan(session: string) {
  await fetch(`/api/map-import?session=${encodeURIComponent(session)}`, {
    method: "DELETE",
    keepalive: true,
    signal: AbortSignal.timeout(10000)
  }).catch(() => {});
}
